// Per-tenant institution-noun resolution for i18next.
//
// Translation values use single-brace tokens `{org}` and `{org_indef}` (i18next
// interpolation uses `{{ }}`, so these are left untouched for us to replace):
//   {org}       → AR definite / EN TitleCase   (الروضة / Kindergarten)
//   {org_indef} → AR indefinite / EN lowercase  (روضة / kindergarten)
//
// Arabic prepositions concatenate cleanly onto the definite form, so composed
// phrases need no special grammar handling (بـ{org}=بالروضة, حافلة {org}=حافلة الروضة).
//
// This module has NO imports on purpose (it must not create an import cycle with
// the auth store or apiClient). The active management type is pushed in via
// `setOrgType`, and bootstrapped once from persisted auth storage below. Keep
// LABELS in sync with the backend MANAGEMENT_TYPES and the Flutter kOrgLabels.

const LABELS = {
  school: { en: ["School", "school"], ar: ["المدرسة", "مدرسة"] },
  kindergarten: { en: ["Kindergarten", "kindergarten"], ar: ["الروضة", "روضة"] },
  summer_camp: {
    en: ["Summer Camp", "summer camp"],
    ar: ["المخيم الصيفي", "مخيم صيفي"],
  },
  course: { en: ["Course", "course"], ar: ["الدورة", "دورة"] },
};

let orgType = "school";

/** Update the active institution type (call on login + settings save). */
export function setOrgType(type) {
  orgType = type && LABELS[type] ? type : "school";
}

/** Bootstrap the type from persisted zustand auth storage (cold start). */
function bootstrapFromStorage() {
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const type = parsed?.state?.kindergartenSettings?.management_type;
    setOrgType(type);
  } catch (_e) {
    // Malformed storage — keep the "school" default.
  }
}
bootstrapFromStorage();

function labelsFor(lng) {
  const lang = lng && lng.toLowerCase().startsWith("ar") ? "ar" : "en";
  return (LABELS[orgType] || LABELS.school)[lang];
}

/**
 * The institution's noun in `lng`, definite — what `{org}` becomes there.
 * For a copy of the app (`PhonePreview`) that speaks the phone's language,
 * whatever the dashboard's.
 */
export function orgNoun(lng) {
  return labelsFor(lng)[0];
}

// i18next postProcessor: replaces the org tokens after interpolation. Registered
// globally (postProcess) so every t() call resolves them — with a fast path that
// skips the ~99% of strings containing no token.
export const orgLabelPostProcessor = {
  type: "postProcessor",
  name: "orgLabel",
  process(value, _key, options, translator) {
    if (typeof value !== "string" || value.indexOf("{org") === -1) {
      return value;
    }
    const lng = (options && options.lng) || (translator && translator.language);
    const [def, indef] = labelsFor(lng);
    return value
      .replace(/\{org_indef\}/g, indef)
      .replace(/\{org\}/g, def);
  },
};
